import {lazy, FC, Suspense} from 'react'
import {Route, Routes, Navigate, useParams} from 'react-router-dom'
import {getDefaultRoute} from '../utils/roleUtils'
import {useAuth} from '../modules/auth'
import {MasterLayout} from '../../_dashboard/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import {DashboardWrapper} from '../pages/dashboard/DashboardWrapper'
import {getCSSVariableValue} from '../../_dashboard/assets/ts/_utils'
import {ID, WithChildren} from '../../_dashboard/helpers'
import HotelsPage from '../pages/hotel-management/HotelsPage'
import {HotelDetailsPage} from '../pages/hotel-management/hotel-details/HotelDetails'
import ReservationsPage from '../pages/reservation-management/ReservationPage'
import {ReservationDetailsPage} from '../pages/reservation-management/reservation-details/ReservationDetails'
import {TourDetailsPage} from '../pages/tours-management/tour-details'
import FooterPage from '../pages/footer-management/FooterPage'
import HeaderPage from '../pages/header-management/HeaderPage'
import ContactPage from '../pages/contact-management/ContactPage'
import AboutPage from '../pages/about-management/AboutPage'
import FacilityPage from '../pages/facilities-managment/FacilitiesPage'
import ThemesPage from '../pages/themes-managment/ThemesPage'
import OptionPage from '../pages/options-managment/OptionPage'
import PagesPage from '../pages/pages-managment/PagesPage'
import {PageInfo} from '../pages/pages-managment/pages-details/PageInfo'
import TourPage from '../pages/tours-management/ToursPage'
import TourReservationsPage from '../pages/tours-management/tour-reservations/TourReservationsPage'
import TourReservationDetailsPage from '../pages/tours-management/tour-reservations/TourReservationDetailsPage'
import TourPromotionsPage from '../pages/tours-management/promotions/TourPromotionsPage'
import ConfigurationsPage from '../pages/tours-management/configurations/ConfigurationsPage'
import UsersPage from '../pages/user-management/UsersPage'
import NewslettersPage from '../pages/newsletter-management/NewslettersPage'
import ClientsPage from '../pages/clients-management/ClientsPage'
import ReportsPage from '../pages/reports-management/ReportsPage'
import ReviewsManagementPage from '../pages/reviews-management/ReviewsManagementPage'
import BlogManagementPage from '../pages/blog-management/BlogManagementPage'
import MailOrdersPage from '../pages/mail-order-management/MailOrdersPage'
import MailOrderFormPage from '../pages/mail-order-management/MailOrderFormPage'
import MailOrderDetailsPage from '../pages/mail-order-management/MailOrderDetailsPage'

const PrivateRoutes = () => {
  const {auth} = useAuth()
  const defaultRoute = getDefaultRoute(auth?.role || localStorage.getItem('role'))

  const ProfilePage = lazy(() => import('../modules/profile/ProfilePage'))
  const AccountPage = lazy(() => import('../modules/accounts/AccountPage'))
  const ChatPage = lazy(() => import('../modules/apps/chat/ChatPage'))

  return (
    <Routes>
      <Route element={<MasterLayout />}>
        {/* Redirect to default page after successful login */}
        <Route path='auth/*' element={<Navigate to={defaultRoute} />} />
        {/* Pages */}
        <Route path='dashboard' element={<DashboardWrapper />} />
        {/* Lazy Modules */}
        <Route
          path='crafted/pages/profile/*'
          element={
            <SuspensedView>
              <ProfilePage />
            </SuspensedView>
          }
        />
        <Route
          path='crafted/account/*'
          element={
            <SuspensedView>
              <AccountPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/chat/*'
          element={
            <SuspensedView>
              <ChatPage />
            </SuspensedView>
          }
        />
        {/* Tours Routes */}
        <Route
          path='apps/tours'
          element={
            <SuspensedView>
              <TourPage />
            </SuspensedView>
          }
        />
        <Route path='apps/tours/new' element={<TourDetailsPage />} />
        <Route path='apps/tours/:id' element={<TourDetailsPage />} />
        <Route
          path='apps/tours/reservations'
          element={
            <SuspensedView>
              <TourReservationsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/tours/reservations/:id'
          element={
            <SuspensedView>
              <TourReservationDetailsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/tours/promotions'
          element={
            <SuspensedView>
              <TourPromotionsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/tours/configurations'
          element={
            <SuspensedView>
              <ConfigurationsPage />
            </SuspensedView>
          }
        />
        {/* End Tours Routes */}
        {/* Mail Orders Routes */}
        <Route
          path='apps/mail-orders'
          element={
            <SuspensedView>
              <MailOrdersPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/mail-orders/new'
          element={
            <SuspensedView>
              <MailOrderFormPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/mail-orders/:id'
          element={
            <SuspensedView>
              <MailOrderDetailsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/mail-orders/:id/edit'
          element={
            <SuspensedView>
              <MailOrderFormPage />
            </SuspensedView>
          }
        />
        {/* End Mail Orders Routes */}
        <Route
          path='apps/user-management/*'
          element={
            <SuspensedView>
              <UsersPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/clients-management/*'
          element={
            <SuspensedView>
              <ClientsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/reports-management/*'
          element={
            <SuspensedView>
              <ReportsPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/reviews-management/*'
          element={
            <SuspensedView>
              <ReviewsManagementPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/blog-management/*'
          element={
            <SuspensedView>
              <BlogManagementPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/hotel-management/*'
          element={
            <SuspensedView>
              <HotelsPage />
            </SuspensedView>
          }
        />

        <Route
          path='apps/reservation-management/*'
          element={
            <SuspensedView>
              <ReservationsPage />
            </SuspensedView>
          }
        />

        <Route path='/hotel/:id' element={<HotelDetailsPage />} />
        <Route path='/reservation/:id' element={<ReservationDetailsPage />} />

        <Route
          path='apps/footer-management/*'
          element={
            <SuspensedView>
              <FooterPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/header-management/*'
          element={
            <SuspensedView>
              <HeaderPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/Contact-management/*'
          element={
            <SuspensedView>
              <ContactPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/about-management/*'
          element={
            <SuspensedView>
              <AboutPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/facilities-management/*'
          element={
            <SuspensedView>
              <FacilityPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/themes-management/*'
          element={
            <SuspensedView>
              <ThemesPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/OPTIONS-management/*'
          element={
            <SuspensedView>
              <OptionPage />
            </SuspensedView>
          }
        />
        <Route
          path='apps/pages-management/*'
          element={
            <SuspensedView>
              <PagesPage />
            </SuspensedView>
          }
        />
        <Route path='pages/:id' element={<PageInfo id={useParams().id! as unknown as ID} />} />

        <Route
          path='apps/newsletter-management/newsletters'
          element={
            <SuspensedView>
              <NewslettersPage />
            </SuspensedView>
          }
        />

        {/* Page Not Found */}
        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
    </Routes>
  )
}

const SuspensedView: FC<WithChildren> = ({children}) => {
  const baseColor = getCSSVariableValue('--bs-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export {PrivateRoutes}
